import {
  formatMinorAmount,
  formatAmountCompact,
  toMajorUnit,
  type CurrencyFormatOptions,
} from '@owner/currency'

import { type FormatFn } from './Format.types'

export interface CurrencyFormatterOptions {
  cents: 'show-always' | 'show-non-zero' | 'hide'
  notation?: 'standard' | 'compact'
  compactUnit?: 'auto' | 'thousands'
}

const defaultCurrencyFormatOptions: CurrencyFormatterOptions = {
  cents: 'show-always',
}

export const CurrencyFormatter = (
  value: number,
  options: CurrencyFormatOptions = { includeSymbol: true }
): string => {
  return formatMinorAmount(value, options)
}

export const getCurrencyFormatter =
  (
    options: CurrencyFormatterOptions = defaultCurrencyFormatOptions
  ): FormatFn<number> =>
  (value: number) => {
    if (options.cents === 'show-non-zero') {
      return formatAmountCompact(value)
    }

    if (options.cents === 'hide') {
      const majorAmount = toMajorUnit(value)

      // Force display in thousands with 1 decimal place
      if (options.compactUnit === 'thousands') {
        const inThousands = majorAmount / 1000
        const formatted = new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 1,
          minimumFractionDigits: 1,
        }).format(inThousands)
        return `$${formatted}K`
      }

      // Using default 'en-US' and 'USD' as bcpTag and currency,
      // consistent with how formatMinorAmount would be called without explicit options.
      // Intl.NumberFormat with style: 'currency' includes the symbol by default.
      const formatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        style: 'currency',
        ...(options.notation === 'compact' && { notation: 'compact' }),
      })
      return formatter.format(majorAmount)
    }

    return CurrencyFormatter(value)
  }
